"use strict";

import React, { useEffect, useCallback, useLayoutEffect, memo, useImperativeHandle, useRef } from "react";
import memoize from "memoize-one";
import { VariableSizeList as List, areEqual } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import Prompt from "./prompt.js";
import Label from "./label.js";
import { useContext } from "react";
import { SchemeContext } from "./scheme.js";
import { useContainerDimensions, calculateRequiredRows } from "./utils.js";

// delay for single vs double click constant
// tested time and 300ms seems most successful
const CLICK_DELAY_MS = 300;

const MIN_GRID_ROW_HEIGHT = 20;
// Initial row height estimate (px) before DOM measurement; aligns with grid cell CSS (~12px × 1.2 + padding)
const EST_LINE_HEIGHT_PX = 14.4;
const EST_CELL_VERTICAL_PADDING_PX = 2;
const EST_MIN_CELL_BODY_PX = 16;
// Long cell values use this threshold so short labels keep the default row height until measured
const WRAP_LENGTH_THRESHOLD = 20;

const Row = memo(function ARow(props) {
    // Data passed to List as "itemData" is available as props.data
    const { grid, extraprops, setValue, gridWidth, notifyRowHeight } = props.data;
    const gridindex = extraprops.gridindex;
    const row = grid.prompts[props.index];
    const scheme = useContext(SchemeContext);

    const clickTimer = useRef();
    const pendingClick = useRef(false);
    const determineRowSelectionTriggerFromClick = (e) => { 
        // Store click information for intelligent processing
        const target = e.target;
        const currentTarget = e.currentTarget;
        // Only consider ACTUAL input elements as interactive (not display DIVs with prompt class)
        const clickedOnInteractiveElement = target && (
            target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.tagName === "BUTTON"
        );
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
            activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.tagName === "SELECT" ||
            activeElement.tagName === "BUTTON"
        );

        const isActiveElementInSameRow = activeElement && currentTarget &&
            activeElement.closest("tr") === currentTarget;

        if (clickedOnInteractiveElement || (isInputFocused && isActiveElementInSameRow)) {
            return;
        }

        onclick(currentTarget);
    };

    const handleClick = (e) => {
        const currentTarget = e.currentTarget;

        // Direct Select (DS): single-click only; commit immediately without delay or pseudo double-click.
        if (grid.controltype === "DS") {
            determineRowSelectionTriggerFromClick(e);
            return;
        }

        const target = e.target;

        const clickedOnInteractiveElement = target && (
            target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.tagName === "BUTTON"
        );

        // Single-click vs double-click flow for SS / ES / MSS / MS / MES — not used for DS.
        if (pendingClick.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
            pendingClick.current = false;
            ondblclick(currentTarget);
            return;   
        }

        pendingClick.current = true;
        clickTimer.current = setTimeout(() => {
            pendingClick.current = false;
            
            // Enhanced check: Don't trigger row selection if user is now focused on an actual input field
            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' || 
                activeElement.tagName === 'TEXTAREA' || 
                activeElement.tagName === 'SELECT' ||
                activeElement.tagName === 'BUTTON'
            );
            
            // Check if the active element is within the same row that was clicked
            const isActiveElementInSameRow = activeElement && currentTarget &&
                activeElement.closest('tr') === currentTarget;
            
            // Don't trigger row selection if:
            // 1. User clicked directly on an interactive element, OR
            // 2. An input field in the same row now has focus (user clicked to focus)
            if (clickedOnInteractiveElement || (isInputFocused && isActiveElementInSameRow)) {
                return;
            }
            
            // Safe to trigger row selection - user clicked on non-interactive area
            onclick(currentTarget);
        }, CLICK_DELAY_MS);
    };

    // clear our timer upon unmount
    useEffect(() => {
       return () => {
           if (clickTimer.current) {
               clearTimeout(clickTimer.current);
           }
           pendingClick.current = false;
       };
    }, []);

    const onclick = (currentTarget) => {
        if (grid.controltype == "SS" || grid.controltype == "ES") {
            WD.ssprocessclick(currentTarget, props.index + 1, false, true, true);
        } else if (grid.controltype == "MSS" || grid.controltype == "MES") {
            WD.ssprocessclick(currentTarget, props.index + 1, false, false, true);
        } else if (grid.controltype == "DS") {
            WD.ssprocessclick(currentTarget, props.index + 1, true, true, true);
        } else if (grid.controltype == "MS") {
            WD.ssprocessclick(currentTarget, props.index + 1, false, false, true);
        }
    };

    const ondblclick = (currentTarget) => {
        if (grid.controltype == "SS" || grid.controltype == "ES") {
            WD.ssprocessclick(currentTarget, props.index + 1, true, true, false);
        } else if (grid.controltype == "MSS" || grid.controltype == "MES") {
            WD.ssprocessclick(currentTarget, props.index + 1, true, true, false);
        } else if (grid.controltype == "DS") {
            return;
        } else if (grid.controltype == "MS") {
            WD.ssprocessclick(currentTarget, props.index + 1, false, false, true);
        }
    };

    let classnames = "gridrow";
    let rowcolour;
    if (grid.controltype == "SS" || grid.controltype == "ES" ||
        grid.controltype == "MSS" || grid.controltype == "DS" || grid.controltype == "MS" 
        || grid.controltype == "MES") {
        classnames += " selectgrid";
        rowcolour = scheme?.EDITABLE?.colour;
    }
    if (props.index % 2) {
        rowcolour = 'rgba(0,0,0,0.06)';
    }

    const trRef = useRef(null);

    useLayoutEffect(() => {
        if (!notifyRowHeight || !trRef.current) {
            return;
        }

        const measureRow = () => {
            const tr = trRef.current;
            if (!tr) {
                return;
            }
            // react-window pins an estimated height on the <tr>. Measuring scrollHeight in that
            // state can match the slot (too small) while the next row overlaps. Briefly drop the
            // fixed height so intrinsic layout sees the full wrapped text, then restore before paint.
            const prevHeight = tr.style.height;
            const prevMinHeight = tr.style.minHeight;
            const prevMaxHeight = tr.style.maxHeight;
            tr.style.height = "auto";
            tr.style.minHeight = "0";
            tr.style.maxHeight = "none";

            let maxContent = MIN_GRID_ROW_HEIGHT;
            const cells = tr.querySelectorAll(":scope > td");
            cells.forEach((td) => {
                const container = td.querySelector(".promptcontainer");
                const textEl = container &&
                    (container.querySelector(".prompttext") || container.querySelector("textarea"));
                const target = textEl || container || td;
                const h = target.scrollHeight;
                if (h > maxContent) {
                    maxContent = h;
                }
            });
            const intrinsicTr = tr.getBoundingClientRect().height;
            maxContent = Math.max(maxContent, intrinsicTr);

            tr.style.height = prevHeight;
            tr.style.minHeight = prevMinHeight;
            tr.style.maxHeight = prevMaxHeight;

            notifyRowHeight(props.index, maxContent);
        };

        measureRow();
        // Second pass: after other rows update via resetAfterIndex, overlap no longer affects layout.
        const raf = requestAnimationFrame(measureRow);
        return () => cancelAnimationFrame(raf);
    }, [
        notifyRowHeight,
        props.index,
        grid.prompts[props.index],
        gridWidth,
        props.style && props.style.height,
    ]);

    return (
        <tr
            ref={trRef}
            data-promptno={grid.startprompt}
            onClick={(e) => handleClick(e)}
            style={{ ...props.style, backgroundColor: rowcolour }}
            className={classnames}
            data-row={props.index+1}
        >
            {row.map((prompt, colndx) => (grid.prompts[0][colndx].visible &&
                <td
                    key={colndx}
                    style={{
                        left: grid.freezecol - 1 >= colndx
                            ? grid.headers[colndx].newwidth
                            : undefined,
                        width: grid.prompts[0].length === 1 ? "" : grid.prompts[0][colndx].width,
                        maxWidth: grid.prompts[0].length === 1 ? "" : grid.prompts[0][colndx].width,
                        minWidth: grid.prompts[0].length === 1 ? "" : grid.prompts[0][colndx].width,
                        backgroundColor: prompt.bgcolour,
                        color: prompt.textcolour || "#FFF"
                    }}
                    className={`
                        ${grid.freezecol - 1 >= colndx ? "freezecol" : ""}
                        ${grid.freezecol - 1 == colndx ? "lastfreezecol" : ""}
                        `}
                >
                    <Prompt
                        key={prompt.name}
                        col={colndx}
                        prompt={{ ...grid.prompts[0][colndx], ...prompt }}
                        grid={true}
                        gridfocus1={grid.gridfocus1}
                        gridfocus2={grid.gridfocus2}
                        gridcontrol={grid.controltype}
                        gridindex={gridindex}
                        gridwidth={gridWidth}
                        row={props.index}
                        screenendrow={extraprops.screenendrow}
                        setValue={setValue}
                    />
                </td>
            ))
            }
        </tr>
    )
}, areEqual);
const createItemData = memoize((grid, extraprops, setValue, gridWidth, notifyRowHeight) => ({
    grid,
    extraprops,
    setValue,
    gridWidth,
    notifyRowHeight,
}));

const DataGrid = React.forwardRef((props, ref) => {
    const hasHeader =
        props.grid.headingstyle !== "F" && props.grid.headingstyle !== "N";
    const listRef = useRef();
    const outerListRef = useRef();
    const tableRef = useRef();
    const rowMeasuredHeightsRef = useRef({});
    const { width: gridWidth } = useContainerDimensions(tableRef);
    const scheme = useContext(SchemeContext);

    const promptValuesDigest = props.grid.prompts
        .map((r) => r.map((p) => p.value).join("\0"))
        .join("\0");

    const notifyRowHeight = useCallback((rowIndex, height) => {
        const isGc = scheme?.isgc === true ||
            (typeof document !== "undefined"
                && document.body.classList.contains("gc"));
        const gcMeasureTrimPx = isGc ? 1 : 0;
        const compensatedHeight = Math.max(0, height - 1 - gcMeasureTrimPx);
        const h = Math.max(MIN_GRID_ROW_HEIGHT, Math.ceil(compensatedHeight));
        const prev = rowMeasuredHeightsRef.current[rowIndex];
        if (prev === h) {
            return;
        }
        rowMeasuredHeightsRef.current[rowIndex] = h;
        listRef.current?.resetAfterIndex(rowIndex);
    }, [scheme?.isgc]);

    const scrollToItem = useCallback((row) => {
        listRef.current?.scrollToItem(row);
    }, []);

    useImperativeHandle(ref, () => ({
        scrollToItem,
    }), [scrollToItem]);

    const itemData = createItemData(
        props.grid,
        props.extraprops,
        props.setValue,
        gridWidth,
        notifyRowHeight
    );

    useEffect(() => {
        rowMeasuredHeightsRef.current = {};
        listRef.current?.resetAfterIndex(0);
    }, [props.grid.prompts, gridWidth]);
    
    // Force recalculation when prompt values change
    useEffect(() => {
        if (listRef.current) {
            rowMeasuredHeightsRef.current = {};
            requestAnimationFrame(() => {
                listRef.current.resetAfterIndex(0);
            });
        }
    }, [promptValuesDigest]);
    
    useEffect(() => {
        if (tableRef.current && gridWidth > 0) {
            requestAnimationFrame(() => {
                if (tableRef.current) {
                    var $gridParent = $(tableRef.current).closest('.grid').parent();
                    if (typeof WD !== 'undefined' && WD.fixgridheader) {
                        WD.fixgridheader($gridParent);
                    }
                    if (outerListRef.current) {
                        $(tableRef.current).find('thead').scrollLeft(outerListRef.current.scrollLeft);
                    }
                }
            });
        }
    }, [gridWidth]);
    
    const getItemSize = (index) => {
        const measured = rowMeasuredHeightsRef.current[index];
        if (measured != null && measured >= MIN_GRID_ROW_HEIGHT) {
            return measured;
        }

        let itemSize = MIN_GRID_ROW_HEIGHT; // Minimum row height

        props.grid.prompts[index].forEach((prompt, colndx) => {
            prompt = { ...props.grid.prompts[0][colndx], ...prompt };

            const raw = prompt.value;
            let decoded = "";
            if (raw) {
                try {
                    decoded = decodeURIComponent(raw);
                } catch {
                    decoded = raw;
                }
            }

            // More aggressive detection - check if any field has content that might need wrapping
            const hasSignificantContent = decoded.length > WRAP_LENGTH_THRESHOLD;
            const hasMultilineContent = raw && (
                raw.includes("%0A") || // URL encoded newline
                raw.includes("\n") || 
                raw.includes("\r") ||
                decoded.includes("\n")
            );

            const needsHeightEstimate = (
                prompt.edittype === "WP" ||
                prompt.edittype === "WORDWRAP" ||
                prompt.edittype === "SCH.SCHEDULE" ||
                hasSignificantContent ||
                hasMultilineContent
            );

            if (needsHeightEstimate && prompt.displaywidth !== 0) {
                const tRequiredRows = calculateRequiredRows(prompt, gridWidth);
                
                // Precise height calculation to match actual content rendering
                // Based on actual CSS: font-size: 12px, line-height: 1.2em = 14.4px per line
                // CSS padding: 2px top + 2px bottom = 4px total vertical padding
                const exactContentHeight = Math.max(
                    EST_MIN_CELL_BODY_PX, // Minimum for single line
                    Math.ceil(EST_LINE_HEIGHT_PX * Math.max(1, tRequiredRows)) + EST_CELL_VERTICAL_PADDING_PX
                );
                
                itemSize = Math.max(itemSize, exactContentHeight);
            }
        });

        return itemSize;
    };

    return (
        <table ref={tableRef}>
            {hasHeader && (
                <thead style={{ backgroundColor: scheme?.BACKGROUND?.colour }}>
                    <tr>
                        {props.grid.headers
                            .map((header, index, headers) => {
                                const previousWidth = headers[index].newwidth;
                                return ( header.width > 0 &&
                                    <th
                                        className={`
                                        ${props.grid.freezecol - 1 >= index ? "freezecol" : ""}
                                        ${props.grid.freezecol - 1 == index ? "lastfreezecol" : ""}
                                    `}
                                        style={{
                                            left: props.grid.freezecol - 1 >= index
                                                ? index == 0
                                                    ? '0'
                                                    : `calc(${previousWidth} - 3px)`
                                                : undefined,
                                            width: header.width + "%", maxWidth: header.width + "%"
                                        }}
                                        colSpan={1}
                                        key={index}
                                    >
                                        <Label tag={decodeURIComponent(header.tag)} />
                                    </th>
                                )
                            })
                        }
                    </tr>
                </thead>
            )}
            <tbody>
                <AutoSizer>
                    {({ height, width }) => (
                        <List
                            ref={listRef}
                            outerRef={outerListRef}
                            height={height - 2}
                            itemCount={props.grid.prompts.length}
                            itemData={itemData}
                            itemSize={getItemSize}
                            overscanCount={
                                Math.ceil(props.grid.endrow - props.grid.startrow)
                            }
                            width={width - 3}
                        >
                            {Row}
                        </List>
                    )}
                </AutoSizer>
            </tbody>
        </table>
    );
});
DataGrid.displayName = "DataGrid";

const Grid = React.forwardRef((props, myref) => {
    const extraprops = {
        gridindex: props.gridindex,
        screenendrow: props.screenendrow,
    };
    const gridref = React.useRef();

    useImperativeHandle(myref, () => ({
        scrollToItem,
    }));

    function scrollToItem(row) {
        gridref.current.scrollToItem(row);
    }

    return (
        <DataGrid
            ref={gridref}
            extraprops={extraprops}
            grid={props.grid}
            setValue={props.setValue}
        />
    );
});
Grid.displayName = "Grid";

export default Grid;
